/**
 * VER UNA COTIZACIÓN: el papel, los datos, y qué hacer con ella.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LAS TRES COSAS QUE SE HACEN CON UN PRESUPUESTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. **Dárselo al cliente** — impreso o por WhatsApp. Es lo que se hace el
 *      99 % de las veces, así que son los botones grandes del pie.
 *   2. **Cobrarlo**, cuando el cliente dice que sí. Ese es el día bueno.
 *   3. **Borrarlo**, cuando no. No hubo dinero: se borra y ya.
 *
 * El camino de imprimir es el MISMO que el del recibo —PDF del ancho del rollo
 * para las térmicas, hoja HTML para la carta— porque esa maquinaria costó dos
 * fallos reales aprenderla. Lo único propio es lo que dice el papel, y eso vive
 * en `papelDeCotizacion.ts`.
 */

import { useMemo, useState } from "react";
import { textoDeSin } from "@/lib/funcionesDelRestaurante";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, MapPin, Printer, Share2, ShoppingCart, Trash2, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Boton, Campo, Nota, cx } from "@/components/ui";
import { Confirmar, Modal } from "@/components/Modal";
import { mensajeDeError } from "@/lib/api";
import { cotizaciones, monedas as endpointMonedas, negocio } from "@/lib/endpoints";
import { llevaMarcaDeExento } from "@/lib/exentos";
import { comoFormato, formatMoney, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import { imprimirHoja, type FormatoDeHoja } from "@/lib/imprimirHoja";
import type { AnchoPapel, PaginaDeCodigos } from "@/lib/escpos";
import { TicketEnLineas } from "@/lib/ticketLineas";
import type { Cotizacion, ItemListRow, Tenant } from "@/lib/types";
import { leerProductos } from "@/lib/db";
import { useCarrito } from "@/stores/carrito";
import { aLaCajaConPreciosDeHoy } from "./aLaCaja";
import {
  enlaceParaElRepartidor,
  entregaDeLaNota,
  mensajeParaElRepartidor,
  nombreDeLaEntrega,
  notaSinEntrega,
} from "./entregaDelPedido";
import { CotizacionEnCarta, cotizacionEnCartaComoPdf, imprimirCotizacionEnCarta } from "./CotizacionEnCarta";
import { enviarPdf } from "@/lib/compartirPdf";
import { useImpresora } from "@/stores/impresora";
import { aviso } from "@/stores/ui";
import {
  componerCotizacion,
  esUnPedido,
  construirCotizacion,
  cotizacionComoPdf,
  soloFecha,
} from "./papelDeCotizacion";

/** El identificador del elemento que se imprime. Lo usa `imprimirHoja`. */
const ID_DEL_PAPEL = "cotizacion";

export function ModalCotizacion({
  cotizacion,
  onCerrar,
  recienCreada = false,
  onCambiada,
}: {
  cotizacion: Cotizacion;
  onCerrar: () => void;
  recienCreada?: boolean;
  /** La cotización cambió aquí dentro (el costo del delivery): la nueva. */
  onCambiada?: (c: Cotizacion) => void;
}) {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const cargarCarrito = useCarrito((e) => e.cargarDesdeCotizacion);

  const { data: datosNegocio } = useQuery({ queryKey: ["negocio"], queryFn: negocio.obtener });
  const { data: listaMonedas } = useQuery({
    queryKey: ["monedas"],
    queryFn: endpointMonedas.listar,
  });

  const config = useImpresora((e) => e.config);
  const conectada = useImpresora((e) => e.conectada);
  const imprimiendo = useImpresora((e) => e.imprimiendo);
  const enviarAImpresora = useImpresora((e) => e.imprimir);

  const [imprimiendoDirecto, setImprimiendoDirecto] = useState(false);
  const [confirmandoBorrar, setConfirmandoBorrar] = useState(false);
  // Los formatos se ELIGEN y la vista previa enseña ese papel; lo imprime
  // «Imprimir» (lo pidió el dueño, 2026-09-29). Igual que en el recibo.
  const [formatoElegido, setFormatoElegido] = useState<FormatoDeHoja | null>(null);

  const hayDirecta = conectada;

  const formatoDe = (codigo: string): FormatoMoneda =>
    (listaMonedas ?? []).map(comoFormato).find((m) => m.code === codigo) ?? {
      code: codigo,
      symbol: "",
      decimals: 2,
      symbol_before: false,
    };
  const dinero = (valor: string, codigo?: string) =>
    formatMoney(valor, formatoDe(codigo ?? cotizacion.currency_code));

  const ancho = (config.ancho ?? "80") as AnchoPapel;
  const formato: FormatoDeHoja = formatoElegido ?? ancho;

  // ── Imprimir ────────────────────────────────────────────────────────────
  function imprimirPdf(formato: FormatoDeHoja): void {
    // La hoja carta no es un rollo: se imprime la que está en pantalla.
    if (formato === "carta") {
      imprimirCotizacionEnCarta();
      return;
    }
    try {
      const pdf = cotizacionComoPdf(cotizacion, datosNegocio, {
        ancho: formato,
        pagina: config.pagina,
        cortarPapel: false,
        monedas: (listaMonedas ?? []).map(comoFormato),
      });
      const url = URL.createObjectURL(pdf);
      /*
        ⚠️ SIN `noopener`, y no es un descuido: con él, `window.open` devuelve
        `null` aunque la pestaña se haya abierto perfectamente —está así en el
        estándar— y ese `null` dispararía el camino de emergencia encima del
        que ya funcionó, imprimiendo dos veces. Es el mismo fallo que ya se
        arregló en el recibo.
      */
      const pestana = window.open(url, "_blank");
      if (!pestana) {
        URL.revokeObjectURL(url);
        imprimirHoja(ID_DEL_PAPEL, formato);
        return;
      }
      // La dirección se libera tarde: soltarla enseguida deja al visor sin el
      // archivo que iba a abrir y la pestaña sale en blanco.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      imprimirHoja(ID_DEL_PAPEL, formato);
    }
  }

  async function imprimirDirecto(): Promise<boolean> {
    setImprimiendoDirecto(true);
    try {
      // Sin imagen en los rollos, como el recibo (2026-09-29).
      const bytes = construirCotizacion(cotizacion, datosNegocio, {
        logo: null,
        ancho: config.ancho,
        pagina: config.pagina,
        cortarPapel: config.cortarPapel,
        monedas: (listaMonedas ?? []).map(comoFormato),
      });
      return await enviarAImpresora(bytes);
    } finally {
      setImprimiendoDirecto(false);
    }
  }

  async function imprimir() {
    if (hayDirecta && formato !== "carta") {
      const salio = await imprimirDirecto();
      if (salio) return;
    }
    imprimirPdf(formato);
  }

  /*
    Mandarla por WhatsApp.

    ⚠️ Para una cotización esto pesa MÁS que imprimir, al revés que en un
    recibo. Un recibo se entrega en la mano; un presupuesto se pide por teléfono
    y se manda por mensaje — y quien lo recibe lo reenvía a quien decide.

    ⚠️ COMO PDF, NO COMO TEXTO (lo pidió el dueño, 2026-10-19): el papel que se
    está viendo, en el formato elegido (hoja carta o rollo). Ver `enviarPdf`.
    El texto queda solo si el PDF no se pudo armar.
  */
  async function compartir() {
    const nombre = `Cotización ${cotizacion.numero}`;
    let pdf: Blob | null = null;
    try {
      pdf =
        formato === "carta"
          ? cotizacionEnCartaComoPdf()
          : cotizacionComoPdf(cotizacion, datosNegocio, {
              ancho: formato,
              pagina: config.pagina,
              cortarPapel: false,
              monedas: (listaMonedas ?? []).map(comoFormato),
            });
    } catch {
      pdf = null;
    }
    if (pdf) {
      await enviarPdf(pdf, nombre);
      return;
    }

    const texto = textoDeLaCotizacion(cotizacion, datosNegocio?.name ?? "", dinero);
    try {
      if (navigator.share) {
        await navigator.share({ title: `Cotización ${cotizacion.numero}`, text: texto });
      } else {
        await navigator.clipboard.writeText(texto);
        aviso.exito("Cotización copiada", "Pégala en WhatsApp o donde quieras.");
      }
    } catch {
      /* Canceló el menú de compartir: no es un error. */
    }
  }

  // ── Pasarla a venta ─────────────────────────────────────────────────────
  /*
    ⚠️ NO se cobra desde aquí: se carga en la caja y se cobra como siempre.

    Cobrar necesita elegir método, moneda, dividir el pago, dar vuelto — toda la
    pantalla de cobrar. Repetir un trozo aquí sería una segunda caja peor, y los
    precios se vuelven a calcular igual con el catálogo de hoy.

    Lo que viaja es el carrito Y el identificador de la cotización, para que al
    cerrar la venta quede apuntado que este presupuesto se cerró.
  */
  async function pasarAVenta() {
    /*
      ⚠️ CON LOS PRECIOS DE HOY, que son los que va a cobrar el servidor.

      Se armaba con los del papel de la cotización: si algo había subido desde
      entonces, la caja pedía un monto y la venta se registraba con otro. Ver
      `aLaCajaConPreciosDeHoy`.
    */
    const ids = cotizacion.items.map((l) => l.item_id).filter((x): x is string => !!x);
    const deHoy = new Map<string, ItemListRow>();
    try {
      for (const fila of await leerProductos(ids)) if (fila) deHoy.set(fila.id, fila);
    } catch {
      // Sin la copia del aparato, se queda lo del papel: es lo de antes.
    }
    const { enLaCaja, cambiaron, sinCatalogo } = aLaCajaConPreciosDeHoy(cotizacion, deHoy);

    cargarCarrito(enLaCaja);
    onCerrar();
    navegar("/vender");

    if (cambiaron > 0) {
      aviso.exito(
        `Cotización ${cotizacion.numero} en la caja`,
        cambiaron === 1
          ? "Un precio cambió desde que se hizo: se cobra con el de hoy. Revísalo antes de cobrar."
          : `${cambiaron} precios cambiaron desde que se hizo: se cobra con los de hoy. Revísalos antes de cobrar.`,
      );
    } else if (sinCatalogo > 0) {
      aviso.exito(
        `Cotización ${cotizacion.numero} en la caja`,
        "Abre Vender con internet una vez para confirmar los precios de hoy antes de cobrar.",
      );
    } else {
      aviso.exito(
        `Cotización ${cotizacion.numero} en la caja`,
        "Revisa el carrito y cobra normal.",
      );
    }
  }

  const borrar = useMutation({
    mutationFn: () => cotizaciones.borrar(cotizacion.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cotizaciones"] });
      aviso.exito("Cotización borrada", `Ya no está la ${cotizacion.numero}.`);
      onCerrar();
    },
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  const yaSeVendio = cotizacion.estado === "convertida";

  return (
    <>
      <Modal
        abierto
        onCerrar={onCerrar}
        titulo={
          recienCreada
            ? "¡Cotización lista!"
            : `${esUnPedido(cotizacion, datosNegocio) ? "Pedido" : "Cotización"} ${cotizacion.numero}`
        }
        descripcion={
          recienCreada
            ? cotizacion.numero
            : new Date(cotizacion.created_at).toLocaleString("es-VE", {
                dateStyle: "long",
                timeStyle: "short",
              })
        }
        /*
          ⚠️ ANCHA SIEMPRE, TAMBIÉN RECIÉN HECHA. Antes se abría estrecha y sin
          las acciones, con la idea de «imprime y sigue vendiendo».

          Era resolver el problema al revés. Quien acaba de hacer una cotización
          está EN MEDIO de esa tarea, no volviendo a otra: le falta imprimirla,
          mandarla por WhatsApp, o pasarla a venta si el cliente dijo que sí ahí
          mismo. Mandarlo a la pantalla de Cotizaciones a buscarla para eso son,
          en palabras del dueño, «pasos de más».
        */
        ancho="lg"
        pie={
          <>
            <Boton variante="secundario" onClick={onCerrar}>
              {recienCreada ? "Seguir vendiendo" : "Cerrar"}
            </Boton>
            <Boton variante="secundario" icono={<Share2 size={16} />} onClick={compartir}>
              Enviar
            </Boton>
            <Boton
              icono={hayDirecta ? <Zap size={16} /> : <Printer size={16} />}
              onClick={() => void imprimir()}
              cargando={imprimiendoDirecto || imprimiendo}
            >
              Imprimir
            </Boton>
          </>
        }
      >
        {/*
          Se dice en el momento, y una sola vez: acaba de desaparecer el carrito
          y en una caja eso se parece demasiado a haber cobrado. Es la frase que
          evita que alguien crea que ya vendió.
        */}
        {recienCreada && (
          <div className="mb-4 rounded-[var(--radius-control)] bg-accent-soft px-3 py-2 text-center">
            <p className="text-xs text-accent">
              Guardada como <strong>{cotizacion.numero}</strong>. No se cobró nada
              ni se descontó del inventario.
            </p>
          </div>
        )}

        {/*
          Los datos a la izquierda, el papel a la derecha. En vertical en el
          celular, con los DATOS primero: los botones de qué hacer con ella
          están ahí, y el papel es la confirmación, no la respuesta.
        */}
        <div className="lg:grid lg:grid-cols-[1fr_auto] lg:items-start lg:gap-6">
          <div className="space-y-3">
            {/*
              Los tres formatos, siempre visibles, y A LA IZQUIERDA (lo pidió el
              dueño, 2026-09-29): debajo del papel, con una cotización larga,
              había que bajar hasta el final para elegir. Arriba de
              los datos: la lista de lo cotizado también puede ser larga. Igual que en el recibo:
              la impresión directa no existe en Firefox ni en Safari, y esconder
              esta fila deja sin salida a quien sí puede imprimir por el diálogo.
            */}
            <div className="rounded-[var(--radius-card)] border border-line bg-surface p-3 print:hidden">
              <p className="mb-1.5 text-xs text-muted">Imprimir en:</p>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { v: "80", t: "Rollo 80 mm" },
                    { v: "58", t: "Rollo 58 mm" },
                    { v: "carta", t: "Hoja carta" },
                  ] as const
                ).map((f) => (
                  <button
                    key={f.v}
                    type="button"
                    onClick={() => setFormatoElegido(f.v)}
                    aria-pressed={f.v === formato}
                    className={cx(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                      f.v === formato
                        ? "border-accent bg-accent-soft text-accent"
                        : "border-line text-muted hover:border-accent hover:text-ink",
                    )}
                  >
                    {f.t}
                  </button>
                ))}
              </div>
              <p className="mt-2.5 text-[11px] leading-relaxed text-faint">
                Se abre ya <strong className="text-muted">del tamaño del papel</strong>.
                Solo dale a imprimir y elige tu impresora.
              </p>
            </div>
            <DatosDeLaCotizacion
              cotizacion={cotizacion}
              dinero={dinero}
              yaSeVendio={yaSeVendio}
              onPasarAVenta={pasarAVenta}
              esPedido={esUnPedido(cotizacion, datosNegocio)}
              onBorrar={() => setConfirmandoBorrar(true)}
              borrando={borrar.isPending}
              moneda={formatoDe(cotizacion.currency_code)}
              onCambiada={(c) => {
                onCambiada?.(c);
                void qc.invalidateQueries({ queryKey: ["cotizaciones"] });
              }}
            />
          </div>

          <div className="mt-4 lg:mt-0">
            {formato === "carta" ? (
              // Al lado de los datos, 400 px; debajo (celular, tablet en
              // vertical), a lo ancho, como el recibo: en 400 fijos la hoja
              // quedaba diminuta en una tablet.
              <div className="mx-auto w-full lg:w-[400px]">
                <CotizacionEnCarta cot={cotizacion} />
              </div>
            ) : (
              <HojaDeLaCotizacion
                cotizacion={cotizacion}
                negocio={datosNegocio}
                ancho={formato as AnchoPapel}
                pagina={config.pagina}
                monedas={(listaMonedas ?? []).map(comoFormato)}
                logo={null}
              />
            )}

          </div>
        </div>
      </Modal>

      <Confirmar
        abierto={confirmandoBorrar}
        onCerrar={() => setConfirmandoBorrar(false)}
        onConfirmar={() => {
          setConfirmandoBorrar(false);
          borrar.mutate();
        }}
        peligroso
        titulo={`¿Borrar la cotización ${cotizacion.numero}?`}
        mensaje="Desaparece del todo. No hubo dinero de por medio, así que no queda rastro de nada — y si el cliente vuelve, se le hace otra."
        textoConfirmar="Borrar"
      />
    </>
  );
}

// =============================================================================
//  EL PAPEL EN PANTALLA
// =============================================================================

/**
 * La hoja, dibujada desde LA MISMA composición que se imprime.
 *
 * ⚠️ Es un dibujante, no un segundo papel. En el recibo esto se aprendió por
 * las malas: había un tercer diseño escrito a mano en JSX, se rediseñó el papel
 * y la pantalla se quedó con el diseño viejo — el dueño aprobaba una cosa
 * mirándola y le salía otra por la impresora.
 */
function HojaDeLaCotizacion({
  cotizacion,
  negocio,
  ancho,
  pagina,
  monedas,
  logo,
}: {
  cotizacion: Cotizacion;
  negocio: Tenant | undefined;
  ancho: AnchoPapel;
  pagina: PaginaDeCodigos;
  monedas: FormatoMoneda[] | undefined;
  logo: string | null | undefined;
}) {
  const { lineas, columnas } = useMemo(() => {
    const t = new TicketEnLineas(ancho);
    componerCotizacion(t, cotizacion, negocio, {
      ancho,
      pagina,
      monedas,
      logo: undefined,
      cortarPapel: false,
    });
    return { lineas: t.resultado(), columnas: t.columnasDelPapel };
  }, [cotizacion, negocio, ancho, pagina, monedas]);

  return (
    <div
      id={ID_DEL_PAPEL}
      className={cx(
        "mx-auto bg-white p-4 font-mono text-[11px] leading-tight text-black",
        "rounded-[var(--radius-control)] border border-line",
      )}
      // El ancho en `ch` y no en píxeles: así la hoja mide lo que mide el rollo
      // —32 o 48 columnas— sin números que cuadrar a mano.
      style={{ width: `calc(${columnas}ch + 2rem)` }}
    >
      {logo && (
        <img
          src={logo}
          alt=""
          className="mx-auto mb-2 max-h-16 object-contain"
          style={{ filter: "grayscale(1) contrast(1.3)" }}
        />
      )}
      {lineas.map((l, i) => (
        <div
          key={i}
          className={cx(
            "whitespace-pre",
            l.alineacion === "centro" && "text-center",
            l.alineacion === "derecha" && "text-right",
            l.negrita && "font-bold",
            l.doble && "text-[15px] leading-snug",
          )}
        >
          {l.texto === "" ? " " : l.texto}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
//  LOS DATOS Y LAS ACCIONES
// =============================================================================

function DatosDeLaCotizacion({
  cotizacion,
  dinero,
  yaSeVendio,
  onPasarAVenta,
  onBorrar,
  borrando,
  esPedido = false,
  moneda,
  onCambiada,
}: {
  cotizacion: Cotizacion;
  dinero: (valor: string, codigo?: string) => string;
  yaSeVendio: boolean;
  onPasarAVenta: () => void;
  onBorrar: () => void;
  borrando: boolean;
  /** Un pedido de restaurante o del autoservicio: se habla de pedido, no de cotización. */
  esPedido?: boolean;
  /** La moneda del pedido, para la casilla del costo del delivery. */
  moneda: FormatoMoneda;
  /** El pedido ya recalculado (con el costo del delivery). */
  onCambiada?: (c: Cotizacion) => void;
}) {
  // Retiro o delivery de la página (2026-11-02): lo de la entrega va en su
  // bloque, y la nota sin esos renglones para no decirlo dos veces.
  const entrega = nombreDeLaEntrega(cotizacion);
  const datosDeEntrega = entregaDeLaNota(cotizacion.nota);
  const nota = entrega ? notaSinEntrega(cotizacion.nota) : cotizacion.nota;
  return (
    <div className="space-y-3">
      {yaSeVendio ? (
        <Nota tono="exito">
          Se cobró con la venta <strong>{cotizacion.venta_numero}</strong>
          {cotizacion.convertida_at && (
            <> el {new Date(cotizacion.convertida_at).toLocaleDateString("es-VE")}</>
          )}
          .
        </Nota>
      ) : cotizacion.vencida && !esPedido ? (
        <Nota tono="aviso">
          Venció el {soloFecha(cotizacion.valida_hasta!)}. Si el
          cliente vuelve, hazle una nueva con los precios de hoy.
        </Nota>
      ) : null}

      <Bloque titulo={esPedido ? "Cliente" : "Para quién"}>
        <Dato etiqueta="Cliente" valor={cotizacion.customer_name ?? "Sin cliente"} />
        {cotizacion.customer_phone && (
          <Dato etiqueta="Teléfono" valor={cotizacion.customer_phone} />
        )}
        {cotizacion.origen === "catalogo_web" ? (
          <>
            <Dato
              etiqueta="La hizo"
              valor={
                cotizacion.contacto
                  ? `${cotizacion.contacto}, desde tu página web`
                  : "Un cliente, desde tu página web"
              }
            />
            {entrega && <Dato etiqueta="Entrega" valor={entrega} />}
          </>
        ) : cotizacion.origen === "autoservicio" ? (
          <>
            <Dato
              etiqueta="Origen"
              valor={
                cotizacion.contacto
                  ? `${cotizacion.contacto}, desde el autoservicio`
                  : "Un cliente, desde el autoservicio"
              }
            />
            {cotizacion.modalidad && (
              <Dato
                etiqueta="Entrega"
                valor={cotizacion.modalidad === "llevar" ? "Para llevar" : "Para comer en el local"}
              />
            )}
          </>
        ) : (
          <Dato etiqueta="La hizo" valor={cotizacion.creada_por_nombre ?? "—"} />
        )}
        {cotizacion.valida_hasta && !esPedido && (
          <Dato
            etiqueta="Válida hasta"
            valor={soloFecha(cotizacion.valida_hasta)}
          />
        )}
      </Bloque>

      {/*
        El delivery de la página (2026-11-02): dónde va, el mapa y el botón
        que le reenvía todo al repartidor por WhatsApp.
      */}
      {cotizacion.modalidad === "delivery" && entrega && (
        <Bloque titulo="Delivery">
          {datosDeEntrega.direccion && <Dato etiqueta="Dirección" valor={datosDeEntrega.direccion} />}
          {datosDeEntrega.referencia && <Dato etiqueta="Referencia" valor={datosDeEntrega.referencia} />}
          {datosDeEntrega.mapa && datosDeEntrega.aproximada && (
            <p className="py-0.5 text-xs text-muted">La ubicación es {datosDeEntrega.aproximada}.</p>
          )}
          <CostoDelDelivery
            cotizacion={cotizacion}
            moneda={moneda}
            dinero={dinero}
            yaSeVendio={yaSeVendio}
            onCambiada={onCambiada}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {datosDeEntrega.mapa && (
              <a href={datosDeEntrega.mapa} target="_blank" rel="noopener noreferrer">
                <Boton variante="secundario" tamano="sm" icono={<MapPin size={15} />}>
                  Abrir en el mapa
                </Boton>
              </a>
            )}
            <a
              href={enlaceParaElRepartidor(mensajeParaElRepartidor(cotizacion, datosDeEntrega))}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Boton variante="secundario" tamano="sm" icono={<Bike size={15} />}>
                Enviar al repartidor
              </Boton>
            </a>
          </div>
        </Bloque>
      )}

      <Bloque titulo={esPedido ? "Detalle del pedido" : "Qué se cotizó"}>
        <div className="divide-y divide-line">
          {cotizacion.items.map((l) => (
            <div key={l.id} className="flex items-start gap-2 py-1.5 text-sm">
              <span className="tabular shrink-0 text-muted">{formatearCantidad(l.qty)}×</span>
              <span className="min-w-0 flex-1">
                <span className="text-ink">{l.name}</span>
                {l.variant_name && <span className="text-muted"> · {l.variant_name}</span>}
                {llevaMarcaDeExento(
                  l.exento_de,
                  cotizacion.impuestos.map((t) => t.tax_id),
                ) && <span className="font-medium text-muted"> (E)</span>}
                {/* En rojo y antes de los extras, como en la cocina. */}
                {(l.sin?.length ?? 0) > 0 && (
                  <span className="block text-xs font-semibold text-danger">
                    {textoDeSin(l.sin!)}
                  </span>
                )}
                {(l.opciones?.length ?? 0) > 0 && (
                  <span className="block text-xs text-ink-soft">
                    {l.opciones!.map((o) => `+ ${o.nombre}`).join(" · ")}
                  </span>
                )}
              </span>
              <span className="tabular shrink-0 text-ink">{dinero(l.line_total)}</span>
            </div>
          ))}
        </div>
      </Bloque>

      {/*
        ⚠️ El aviso del IGTF también AQUÍ, no solo en el papel.

        Quien mira la pantalla para decir un precio por teléfono no tiene el
        papel delante. Si el número que ve no menciona la condición, la dice
        mal — y el cliente llega esperando pagar lo que le dijeron.
      */}
      {cotizacion.impuestos.some((t) => !t.suma_al_total) && (
        <Nota tono="aviso">
          {cotizacion.impuestos
            .filter((t) => !t.suma_al_total)
            .map((t) => (
              <span key={t.id} className="block">
                Si paga en {(t.currency_codes ?? []).join(" o ")} se suman{" "}
                <strong>{dinero(t.monto)}</strong> de {t.name} — total{" "}
                {dinero(String(toNumber(cotizacion.total) + toNumber(t.monto)))}.
              </span>
            ))}
        </Nota>
      )}

      {/* Renglón por renglón: la de la página trae un dato en cada uno. */}
      {nota && (
        <Bloque titulo="Nota">
          <p className="whitespace-pre-line text-sm leading-relaxed text-ink-soft">{nota}</p>
        </Bloque>
      )}

      {/* ── Qué hacer con ella ─────────────────────────────────────────── */}
      {!yaSeVendio && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Boton icono={<ShoppingCart size={16} />} onClick={onPasarAVenta}>
            {esPedido ? "Cobrar en la caja" : "Pasar a venta"}
          </Boton>
          <Boton
            variante="peligro"
            icono={<Trash2 size={16} />}
            onClick={onBorrar}
            cargando={borrando}
          >
            Borrar
          </Boton>
        </div>
      )}
    </div>
  );
}

/**
 * El costo del delivery, que pone quien atiende según la dirección
 * (2026-11-02). Lo pidió el dueño: «con la dirección, el que esté atendiendo
 * ponga el precio y lo pongan al procesar el pedido».
 *
 * Se guarda en el pedido como una línea «Delivery» y el total se recalcula;
 * la caja lo cobra con ese monto, sin permiso de cambiar precios.
 */
function CostoDelDelivery({
  cotizacion,
  moneda,
  dinero,
  yaSeVendio,
  onCambiada,
}: {
  cotizacion: Cotizacion;
  moneda: FormatoMoneda;
  dinero: (valor: string, codigo?: string) => string;
  yaSeVendio: boolean;
  onCambiada?: (c: Cotizacion) => void;
}) {
  const actual = cotizacion.items.find((l) => l.monto != null)?.monto ?? null;
  const [texto, setTexto] = useState(actual != null && toNumber(actual) > 0 ? String(toNumber(actual)) : "");
  const guardar = useMutation({
    mutationFn: () => cotizaciones.delivery(cotizacion.id, String(toNumber(texto) || 0)),
    onSuccess: (c) => {
      onCambiada?.(c);
      const nuevo = c.items.find((l) => l.monto != null)?.monto;
      aviso.exito(
        nuevo ? "Costo del delivery guardado" : "Costo del delivery quitado",
        `Total del pedido: ${dinero(c.total)}`,
      );
    },
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  if (yaSeVendio) {
    return actual != null ? <Dato etiqueta="Costo del delivery" valor={dinero(actual)} /> : null;
  }

  const cambio = Math.abs((toNumber(texto) || 0) - (actual != null ? toNumber(actual) : 0)) > 0.00005;
  return (
    <div className="mt-2 border-t border-line pt-2">
      <label className="mb-1 block text-xs text-muted" htmlFor="costo-del-delivery">
        Costo del delivery
      </label>
      <div className="flex items-start gap-2">
        <div className="w-36">
          <Campo
            id="costo-del-delivery"
            type="number"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="0.00"
            min="0"
            moneda={moneda}
            inputMode="decimal"
            className="tabular"
          />
        </div>
        <Boton
          variante="secundario"
          onClick={() => guardar.mutate()}
          cargando={guardar.isPending}
          disabled={!cambio}
        >
          Guardar
        </Boton>
      </div>
      <p className="mt-1 text-xs text-muted">
        {actual != null
          ? "Incluido en el total del pedido. Se cobra en la caja junto con los productos."
          : "Según la dirección. Se suma al total del pedido y se cobra en la caja."}
      </p>
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-surface-2 p-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        {titulo}
      </p>
      {children}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className="text-muted">{etiqueta}</span>
      <span className="text-right text-ink">{valor}</span>
    </div>
  );
}

function formatearCantidad(qty: string): string {
  const n = toNumber(qty);
  return Number.isInteger(n) ? String(n) : n.toLocaleString("es-VE", { maximumFractionDigits: 3 });
}

/**
 * La cotización en texto, para WhatsApp.
 *
 * ⚠️ Lleva la fecha de vencimiento, que es lo que la distingue de una lista de
 * precios suelta. Un mensaje con los totales y sin el «vale hasta el…» es una
 * promesa sin fecha, y es exactamente lo que este documento existe para evitar.
 */
function textoDeLaCotizacion(
  cot: Cotizacion,
  nombreNegocio: string,
  dinero: (valor: string, codigo?: string) => string,
): string {
  const lineas: string[] = [
    `*${nombreNegocio}*`,
    `Cotización ${cot.numero}`,
    new Date(cot.created_at).toLocaleDateString("es-VE"),
    "",
  ];

  if (cot.customer_name) lineas.push(`Para: ${cot.customer_name}`, "");

  const cotizados = cot.impuestos.map((t) => t.tax_id);
  for (const l of cot.items) {
    const marca = llevaMarcaDeExento(l.exento_de, cotizados) ? " (E)" : "";
    lineas.push(`${formatearCantidad(l.qty)} x ${l.name}${marca} — ${dinero(l.line_total)}`);
  }

  lineas.push("", `*TOTAL: ${dinero(cot.total)} ${monedaImpresa(cot.currency_code)}*`);

  for (const t of cot.impuestos.filter((x) => !x.suma_al_total)) {
    lineas.push(
      `Si paga en ${(t.currency_codes ?? []).map(monedaImpresa).join("/")}: +${dinero(t.monto)} de ${t.name}`,
    );
  }

  if (cot.valida_hasta) {
    lineas.push("", `Válida hasta el ${soloFecha(cot.valida_hasta)}.`);
  }
  lineas.push("Esto es una cotización, no una factura.");

  return lineas.join("\n");
}
